import { PROMPT_SETTINGS_KEYS } from './promptSettings.js'

export const SETTINGS_GROUPS = [
  'providers',
  'models',
  'runtime',
  'display',
  'language',
  'media',
  'memory',
  'modules',
  'agents',
  'advanced',
  'sidebar',
  'account',
  'data',
  'prompt',
] as const

export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]

export type SettingsGroupProjectionEpochs = Partial<Record<SettingsGroup, number>>

export const MODEL_PROFILE_SETTINGS_KEYS = [
  'providerCredentials',
  'modelProfiles',
  'modelProfileOrder',
  'modelRoleProfiles',
  'modelRuntimeDefaults',
] as const

export function isModelProfileSettingsGroup(group: SettingsGroup): group is 'providers' | 'models' {
  return group === 'providers' || group === 'models'
}

export const SERVER_SETTINGS_GROUP_BY_KEY: Record<string, SettingsGroup> = {
  account: 'account',
  adaptiveThinkingEffort: 'runtime',
  additionalPrompt: 'advanced',
  aiModel: 'providers',
  ainconfig: 'providers',
  additionalParams: 'providers',
  applyAdditionalParamsToAll: 'providers',
  alwaysScrollToNewMessage: 'sidebar',
  animationSpeed: 'display',
  reducedMotion: 'display',
  hypaV3ProgressOpenChatOnly: 'display',
  antiClaudeOverload: 'runtime',
  apiType: 'providers',
  autoAcquireDisconnectedWriter: 'sidebar',
  askRemoval: 'sidebar',
  assetMaxDifference: 'advanced',
  assetWidth: 'display',
  authRefreshes: 'providers',
  autoContinueChat: 'runtime',
  autoContinueMinTokens: 'runtime',
  autoScrollToNewMessage: 'sidebar',
  autoTranslateCachedOnly: 'language',
  autoTranslateNotificationDeferCapSeconds: 'display',
  automaticCachePoint: 'runtime',
  autofillRequestUrl: 'advanced',
  allowAllExtentionFiles: 'advanced',
  auxModelUnderModelSettings: 'advanced',
  banCharacterset: 'advanced',
  bardWiki: 'memory',
  bias: 'providers',
  blockquoteStyling: 'display',
  botSettingAtStart: 'sidebar',
  bulkEnabling: 'advanced',
  chainOfThought: 'runtime',
  checkCorruption: 'advanced',
  claude1HourCaching: 'providers',
  claudeAPIKey: 'providers',
  claudeAws: 'providers',
  claudeCachingExperimental: 'providers',
  clickToEdit: 'sidebar',
  coldstorage: 'advanced',
  colorScheme: 'display',
  colorSchemeName: 'display',
  customColorScheme: 'display',
  customTextTheme: 'display',
  cohereAPIKey: 'providers',
  combineTranslation: 'language',
  comfyConfig: 'media',
  comfyUiUrl: 'media',
  createFolderOnBranch: 'sidebar',
  currentPluginProvider: 'providers',
  customAPIFormat: 'providers',
  customBackground: 'display',
  chatDisplayTailCount: 'display',
  chatLoadInitialPages: 'display',
  chatLoadAdditionalPages: 'display',
  chatScreenWidth: 'display',
  chatGenerationTogglePresets: 'sidebar',
  classicMaxWidth: 'display',
  customCSS: 'display',
  customFont: 'display',
  customGUI: 'display',
  customModels: 'providers',
  customSidebarItems: 'sidebar',
  customProxyRequestModel: 'providers',
  customQuotes: 'display',
  customQuotesData: 'display',
  customFlags: 'advanced',
  customTokenizer: 'providers',
  dallEQuality: 'media',
  deeplOptions: 'language',
  deeplXOptions: 'language',
  deepseekReasoningEffort: 'runtime',
  deepseekThinkingType: 'runtime',
  descriptionPrefix: 'advanced',
  desktopSidebarColumns: 'display',
  didFirstSetup: 'account',
  disableAutoPopupMessageEditor: 'sidebar',
  disableSeperateParameterChangeOnPresetChange: 'runtime',
  doNotChangeSeperateModels: 'runtime',
  doNotWarnExternalServers: 'advanced',
  dynamicAssets: 'media',
  dynamicAssetsEditDisplay: 'media',
  dynamicModelRegistry: 'providers',
  elevenLabKey: 'media',
  emotionProcesser: 'media',
  emotionPrompt2: 'advanced',
  enabledModules: 'modules',
  moduleFolders: 'modules',
  enableBlockPartialEdit: 'sidebar',
  enableBookmark: 'advanced',
  enableCustomFlags: 'advanced',
  enableDevTools: 'advanced',
  enableDragPartialEdit: 'sidebar',
  enableLorebookStubs: 'advanced',
  enableRemoteSaving: 'advanced',
  enableRisuaiProTools: 'sidebar',
  enableScrollToActiveChar: 'advanced',
  epEnabled: 'runtime',
  echoDelay: 'providers',
  echoMessage: 'providers',
  falLora: 'media',
  falLoraName: 'media',
  falLoraScale: 'media',
  falModel: 'media',
  falToken: 'media',
  fishSpeechKey: 'media',
  fixedChatTextarea: 'sidebar',
  floatingChatInput: 'sidebar',
  font: 'display',
  forceReplaceUrl: 'providers',
  frequencyPenalty: 'runtime',
  fullScreen: 'display',
  genTime: 'runtime',
  generationSeed: 'runtime',
  goCharacterOnImport: 'sidebar',
  google: 'providers',
  globalChatVariables: 'sidebar',
  gptVisionQuality: 'media',
  guiHTML: 'display',
  globalscript: 'advanced',
  hamburgerButtonBottom: 'sidebar',
  heightMode: 'display',
  hideAllImages: 'display',
  hideApiKey: 'display',
  hideRealm: 'display',
  hordeConfig: 'providers',
  hotkeys: 'sidebar',
  htmlTranslation: 'language',
  huggingfaceKey: 'providers',
  hypaCustomSettings: 'memory',
  hypaMemoryKey: 'memory',
  hypaModel: 'memory',
  memoryAlgorithmType: 'memory',
  supaModelType: 'memory',
  hypaMemory: 'memory',
  hypav2: 'memory',
  hanuraiEnable: 'memory',
  legacyMemoryMigrationNoticeDismissed: 'memory',
  hypaV3: 'memory',
  hypaV3Key: 'memory',
  hypaV3PresetId: 'memory',
  hypaV3Presets: 'memory',
  selectedHypaV3PresetId: 'memory',
  iconsize: 'display',
  imageCompression: 'media',
  ImagenAspectRatio: 'media',
  ImagenImageSize: 'media',
  ImagenModel: 'media',
  ImagenPersonGeneration: 'media',
  inlayErrorResponse: 'advanced',
  inputHooks: 'advanced',
  instructChatTemplate: 'providers',
  instantRemove: 'sidebar',
  JinjaTemplate: 'providers',
  jailbreakToggle: 'sidebar',
  keepSessionAlive: 'advanced',
  keiServerURL: 'advanced',
  koboldURL: 'providers',
  language: 'language',
  lastLoadedLoadoutName: 'sidebar',
  legacyMediaFindings: 'media',
  legacyTranslation: 'language',
  lineHeight: 'display',
  localActivationInGlobalLorebook: 'sidebar',
  localStopStrings: 'runtime',
  longPressToPopupEditor: 'sidebar',
  loreBookDepth: 'advanced',
  loreBookToken: 'advanced',
  mancerHeader: 'providers',
  maxContext: 'runtime',
  maxResponse: 'runtime',
  memoryLimitThickness: 'display',
  menuSideBar: 'display',
  mobileSidebarColumns: 'display',
  min_p: 'runtime',
  mistralKey: 'providers',
  modelRuntimeDefaults: 'providers',
  modelProfiles: 'providers',
  modelProfileOrder: 'providers',
  modelRoleProfiles: 'providers',
  providerCredentials: 'providers',
  modelRoles: 'providers',
  modelTools: 'providers',
  moduleIntergration: 'advanced',
  NAIadventure: 'providers',
  NAIApiKey: 'media',
  NAIappendName: 'providers',
  NAII2I: 'media',
  NAIImgConfig: 'media',
  NAIImgModel: 'media',
  NAIImgUrl: 'media',
  NAIREF: 'media',
  NAIsettings: 'providers',
  nanogptKey: 'providers',
  nanogptProvider: 'providers',
  nanogptRequestModel: 'providers',
  nanogptRequestModelName: 'providers',
  nanogptSubscriptionState: 'providers',
  nanogptUseSubscriptionEndpoint: 'providers',
  newImageHandlingBeta: 'media',
  newMessageButtonStyle: 'sidebar',
  newOAIHandle: 'runtime',
  noWaitForTranslate: 'language',
  notification: 'display',
  paragraphBreakBySentences: 'display',
  paragraphBreakSentenceCount: 'display',
  novelai: 'providers',
  novellistAPI: 'providers',
  OaiCompAPIKeys: 'providers',
  ollamaApiKey: 'providers',
  ollamaCloudModel: 'providers',
  ollamaCloudModelName: 'providers',
  ollamaInputMode: 'providers',
  ollamaModel: 'providers',
  ollamaModelName: 'providers',
  ollamaModelSource: 'providers',
  ollamaRequestFormat: 'providers',
  ollamaThinkingMode: 'providers',
  ollamaURL: 'providers',
  ooba: 'providers',
  openAIKey: 'providers',
  openAIFlexProcessing: 'providers',
  openaiCompatImage: 'media',
  openrouterFallback: 'providers',
  openrouterKey: 'providers',
  openrouterMiddleOut: 'providers',
  openrouterProvider: 'providers',
  openrouterRequestModel: 'providers',
  useInstructPrompt: 'providers',
  personaNote: 'advanced',
  playMessage: 'display',
  playMessageOnTranslateEnd: 'display',
  pluginCompatibilityMode: 'advanced',
  strictScriptCheck: 'advanced',
  complexRegexCompatibilityMode: 'advanced',
  complexRegexInputTimeoutMs: 'advanced',
  complexRegexOutputTimeoutMs: 'advanced',
  complexRegexDisplayTimeoutMs: 'advanced',
  regexOutputSizeLimitMiB: 'advanced',
  pluginDevelopMode: 'advanced',
  PresensePenalty: 'runtime',
  presetChain: 'advanced',
  promptInfoInsideChat: 'advanced',
  promptDiffPrefs: 'display',
  promptTextInfoInsideChat: 'advanced',
  proxyKey: 'providers',
  proxyRequestModel: 'providers',
  realmDirectOpen: 'advanced',
  reasoningEffort: 'runtime',
  rememberToolUsage: 'runtime',
  removeIncompleteResponse: 'runtime',
  repetition_penalty: 'runtime',
  requestInfoInsideChat: 'sidebar',
  requestLocation: 'runtime',
  requestRetrys: 'runtime',
  requestHistoryLimit: 'data',
  returnCSSError: 'advanced',
  reverseProxyOobaArgs: 'providers',
  reverseProxyOobaMode: 'providers',
  roundIcons: 'display',
  saveSignatures: 'advanced',
  sdCFG: 'media',
  sdConfig: 'media',
  sdProvider: 'media',
  sdSteps: 'media',
  sendWithEnter: 'sidebar',
  seperateModels: 'runtime',
  seperateModelsForAxModels: 'runtime',
  seperateParameters: 'runtime',
  seperateParametersByModel: 'runtime',
  seperateParametersEnabled: 'runtime',
  settingsCloseButtonSize: 'display',
  showDeprecatedTriggerV1: 'advanced',
  showDeprecatedTriggerV2: 'advanced',
  showFirstMessagePages: 'display',
  showFolderName: 'display',
  useBardWiki: 'advanced',
  showGlobalLorebookAndRegex: 'advanced',
  showMemoryLimit: 'display',
  showMenuChatList: 'sidebar',
  showMenuHypaMemoryModal: 'memory',
  showPromptComparison: 'display',
  showSavingIcon: 'display',
  showTranslationLoading: 'language',
  showUnrecommended: 'advanced',
  sideBarSize: 'display',
  sideMenuRerollButton: 'sidebar',
  simplifiedToolUse: 'runtime',
  stabilityKey: 'media',
  stabilityModel: 'media',
  stabllityStyle: 'media',
  streamGeminiThoughts: 'runtime',
  subModel: 'providers',
  swipe: 'sidebar',
  temperature: 'runtime',
  textAreaSize: 'display',
  textAreaTextSize: 'display',
  textBorder: 'display',
  textgenWebUIBlockingURL: 'providers',
  textgenWebUIStreamURL: 'providers',
  textScreenBorder: 'display',
  textScreenColor: 'display',
  textScreenRounded: 'display',
  textTheme: 'display',
  theme: 'display',
  thinkingTokens: 'runtime',
  thinkingType: 'runtime',
  toggleConfirmRecommendedPreset: 'advanced',
  top_a: 'runtime',
  top_k: 'runtime',
  top_p: 'runtime',
  translateBeforeHTMLFormatting: 'language',
  translatorSendTextAsIs: 'language',
  translatorExcludeThoughts: 'language',
  translator: 'language',
  translatorInputLanguage: 'language',
  translatorHistoryMaxTokens: 'language',
  translatorMaxResponse: 'language',
  translatorPrompt: 'language',
  translatorType: 'language',
  ttsAutoSpeech: 'media',
  unformatQuotes: 'display',
  useAdditionalAssetsPreview: 'display',
  useAutoSuggestions: 'runtime',
  useAutoTranslateInput: 'language',
  useChatCopy: 'display',
  useChatSticker: 'display',
  useExperimental: 'advanced',
  useExperimentalGoogleTranslator: 'language',
  useLegacyGUI: 'display',
  useMonacoEditorOnDesktop: 'sidebar',
  useMonacoEditorOnMobile: 'sidebar',
  usePlainFetch: 'runtime',
  useSayNothing: 'advanced',
  halfStreaming: 'runtime',
  useStreaming: 'runtime',
  useTokenizerCaching: 'advanced',
  username: 'account',
  vertexAccessToken: 'providers',
  vertexAccessTokenExpires: 'providers',
  vertexClientEmail: 'providers',
  vertexPrivateKey: 'providers',
  vertexRegion: 'providers',
  verbosity: 'runtime',
  voicevoxUrl: 'media',
  voyageApiKey: 'memory',
  waifuWidth: 'display',
  waifuWidth2: 'display',
  wavespeedImage: 'media',
  webUiUrl: 'media',
  zoomsize: 'display',
  ...Object.fromEntries(PROMPT_SETTINGS_KEYS.map((key) => [key, 'prompt'])),
}

export const SERVER_SETTINGS_KEYS_BY_GROUP = Object.fromEntries(
  SETTINGS_GROUPS.map((group) => [
    group,
    group === 'agents'
      ? ['agents', 'agentPresets', 'agentPresetDefaultId']
      : group === 'models'
        ? [...MODEL_PROFILE_SETTINGS_KEYS]
        : group === 'language' || group === 'advanced'
          ? [
              ...Object.entries(SERVER_SETTINGS_GROUP_BY_KEY)
                .filter(([, owner]) => owner === group)
                .map(([key]) => key),
              // These compatibility values have separate write/round-trip
              // ownership. Narrow reads must not silently omit their live use.
              ...(group === 'language' ? ['translatorPresetId'] : ['igpPrompt']),
            ]
          : Object.entries(SERVER_SETTINGS_GROUP_BY_KEY)
              .filter(([, owner]) => owner === group)
              .map(([key]) => key),
  ]),
) as Record<SettingsGroup, string[]>

export function isSettingsGroup(value: unknown): value is SettingsGroup {
  return typeof value === 'string' && SETTINGS_GROUPS.includes(value as SettingsGroup)
}
